import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from '@mui/material'
import { criticalityLabel, criticalityTint, PENALTY_SEVERITIES, legislationTypeMeta } from 'features/compliance/obligationConstants'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import AssigneeAvatars from 'components/AssigneeAvatars'
import ConceptModelDiagram from 'features/taskManagement/ConceptModelDiagram'
import OnceCallout from 'features/compliance/OnceCallout'
import ComplianceSetupExplainer from 'features/compliance/ComplianceSetupExplainer'
import SafetyRolesSetup from 'features/compliance/SafetyRolesSetup'
import TaskQuickEditModal from 'features/compliance/TaskQuickEditModal'
import { RAG as RAG_LEVELS, scoreColour, severityColour } from 'features/dashboard/charts/scoreColour'
import SearchableSelect from 'components/SearchableSelect'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import { propOr } from 'ramda'
import VerifiedRoundedIcon           from '@mui/icons-material/VerifiedRounded'
import ScheduleRoundedIcon           from '@mui/icons-material/ScheduleRounded'
import RateReviewRoundedIcon          from '@mui/icons-material/RateReviewRounded'
import SearchRoundedIcon              from '@mui/icons-material/SearchRounded'
import ErrorOutlineRoundedIcon       from '@mui/icons-material/ErrorOutlineRounded'
import BlockRoundedIcon              from '@mui/icons-material/BlockRounded'
import CheckCircleRoundedIcon        from '@mui/icons-material/CheckCircleRounded'
import HelpOutlineRoundedIcon        from '@mui/icons-material/HelpOutlineRounded'
import EventBusyRoundedIcon          from '@mui/icons-material/EventBusyRounded'
import LaunchRoundedIcon             from '@mui/icons-material/LaunchRounded'
import ReplayRoundedIcon             from '@mui/icons-material/ReplayRounded'
import InsertDriveFileRoundedIcon    from '@mui/icons-material/InsertDriveFileRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import BuildRoundedIcon              from '@mui/icons-material/BuildRounded'
import UploadFileRoundedIcon         from '@mui/icons-material/UploadFileRounded'
import ShieldRoundedIcon             from '@mui/icons-material/ShieldRounded'
import SettingsRoundedIcon           from '@mui/icons-material/SettingsRounded'
import AccessTimeRoundedIcon         from '@mui/icons-material/AccessTimeRounded'
import LocalFireDepartmentRounded    from '@mui/icons-material/LocalFireDepartmentRounded'
import WaterDropRoundedIcon          from '@mui/icons-material/WaterDropRounded'
import OutlinedFlagRoundedIcon       from '@mui/icons-material/OutlinedFlagRounded'
import AddCircleOutlineRoundedIcon   from '@mui/icons-material/AddCircleOutlineRounded'
import AutoAwesomeRoundedIcon        from '@mui/icons-material/AutoAwesomeRounded'
import LinkRoundedIcon               from '@mui/icons-material/LinkRounded'
import TaskAltRoundedIcon            from '@mui/icons-material/TaskAltRounded'
import ManageAccountsRoundedIcon     from '@mui/icons-material/ManageAccountsRounded'
import { fetchProgramme, updateProgrammeObligation } from 'services/ProgrammeService'
import { reviewObligationEvidence } from 'services/checklistService'
import ActionsAPI from 'services/ActionsAPI'
import { frequencyLabel } from 'utils/frequency'
import AuditChip from 'features/compliance/AuditChip'
import ObligationProvenance from 'features/compliance/ObligationProvenance'
import FlagObligationModal from 'features/compliance/FlagObligationModal'
import AddProgrammeObligationModal from 'features/compliance/AddProgrammeObligationModal'
import LinkEvidenceModal from 'features/compliance/LinkEvidenceModal'
import LogCompletionModal from 'features/compliance/LogCompletionModal'
import PerUnitEvidenceModal from 'features/compliance/PerUnitEvidenceModal'
import PerUnitSatisfyExplainer from 'features/compliance/PerUnitSatisfyExplainer'
import AiActionExplainer from 'components/AiActionExplainer'
import ObligationHistoryModal from 'features/compliance/ObligationHistoryModal'
import CustomiseObligationModal from 'features/compliance/CustomiseObligationModal'
import ObligationScopePill from 'features/compliance/ObligationScopePill'
import { fetchBpUnits } from 'services/unitsService'
import { notifyComplianceChanged, useComplianceChanged } from 'utils/complianceEvents'
import { refreshRemedialCounts } from 'features/actions/ducks/refreshRemedialCounts'
import MapExistingFilesModal from 'features/compliance/MapExistingFilesModal'
import GenerateFormFromDocument, { FormGenButton } from 'features/taskManagement/components/GenerateFormFromDocument'

const DOMAIN_ICONS = {
  shield:   ShieldRoundedIcon,
  settings: SettingsRoundedIcon,
  clock:    AccessTimeRoundedIcon,
  alert:    LocalFireDepartmentRounded,
  water:    WaterDropRoundedIcon,
}

const severityFromWeight = w => w >= 4 ? 'critical' : w === 3 ? 'high' : w === 2 ? 'medium' : 'low'

const obligationReq = o =>
  o.obligationKind !== 'file_type' ? 'task' : (o.scope === 'unit' ? 'unit' : 'building')

const isNeedsReview = o =>
  !o.reviewedAt && !o.isSkipped && (o.status === 'pending' || o.status === 'expired') && !o.openTask

const critLevelOf = o => {
  if (o.criticality) return String(o.criticality).toLowerCase()
  const w = o.criticalityWeight ?? 1
  return w >= 4 ? 'critical' : w === 3 ? 'high' : w === 2 ? 'medium' : 'low'
}

const REQ_OPTIONS = [
  { value: 'all', label: 'All requirements' },
  { value: 'building', label: 'Whole-building file' },
  { value: 'unit', label: 'Per-unit file' },
  { value: 'task', label: 'Task' },
]
const CRIT_OPTIONS = [
  { value: 'all', label: 'All criticalities' },
  { value: 'critical', label: 'Critical', color: severityColour('critical') },
  { value: 'high', label: 'High', color: severityColour('high') },
  { value: 'medium', label: 'Medium', color: severityColour('medium') },
  { value: 'low', label: 'Low', color: severityColour('low') },
]

const STATUS_META = {
  satisfied: { label: 'Satisfied',     colour: RAG_LEVELS.low.fg, Icon: VerifiedRoundedIcon          },
  in_review: { label: 'Awaiting review', colour: RAG_LEVELS.medium.fg, Icon: RateReviewRoundedIcon     },
  pending:   { label: 'Awaiting evidence', colour: RAG_LEVELS.medium.fg, Icon: ScheduleRoundedIcon       },
  expired:   { label: 'Expired',       colour: RAG_LEVELS.critical.fg, Icon: ErrorOutlineRoundedIcon      },
  overdue:   { label: 'Overdue',       colour: RAG_LEVELS.critical.fg, Icon: EventBusyRoundedIcon         },
  skipped:   { label: 'Not applicable', colour: RAG_LEVELS.none.fg, Icon: BlockRoundedIcon            },
}

const ProgrammeBoard = ({ bpId, embedded = false, onProgrammeLoaded }) => {
  const navigate = useNavigate()
  const [programme, setProgramme] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busyObligationId, setBusyObligationId] = useState(null)
  const [remedialBusy, setRemedialBusy] = useState(null)
  const [unitReviewBusy, setUnitReviewBusy] = useState(null)
  const [unitDenyTarget, setUnitDenyTarget] = useState(null)
  const [unitDenyReason, setUnitDenyReason] = useState('')
  const [remedialTarget, setRemedialTarget] = useState(null)
  const [slow, setSlow] = useState(false)
  const [skipTarget, setSkipTarget] = useState(null)
  const [skipReason, setSkipReason] = useState('')
  const [flagTarget, setFlagTarget] = useState(null)
  const [addObligation, setAddObligation] = useState(null)
  const [linkTarget, setLinkTarget] = useState(null)
  const [logTarget, setLogTarget] = useState(null)
  const [historyTarget, setHistoryTarget] = useState(null)
  const [customiseTarget, setCustomiseTarget] = useState(null)
  const [perUnitTarget, setPerUnitTarget] = useState(null)
  const [unitNames, setUnitNames] = useState(null)
  const unitNamesRequestedRef = useRef(false)
  const ensureUnitNames = useCallback(() => {
    if (unitNamesRequestedRef.current || !bpId) return
    unitNamesRequestedRef.current = true
    fetchBpUnits({ bpId })
      .then(res => {
        const list = res?.data?.data || res?.data || []
        const map = {}
        ;(Array.isArray(list) ? list : []).forEach(u => { map[u.id] = u.name || u.displayName || u.address || 'Unit' })
        setUnitNames(map)
      })
      .catch(() => setUnitNames({}))
  }, [bpId])
  const [mapFiles, setMapFiles] = useState(false)
  const [search, setSearch] = useState('')
  const [searchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || null)
  const [domainFilter, setDomainFilter] = useState(() => searchParams.get('domain') || 'all')
  const targetObligationId = searchParams.get('obligation')
  const [critFilter, setCritFilter] = useState('all')
  const [reqFilter, setReqFilter] = useState('all')
  const [collapsed, setCollapsed] = useState({})
  const [highlightedObligationId, setHighlightedObligationId] = useState(null)
  const [showHelp, setShowHelp] = useState(false)

  const onLoadedRef = useRef(onProgrammeLoaded)
  useEffect(() => { onLoadedRef.current = onProgrammeLoaded }, [onProgrammeLoaded])

  const loadSeqRef = useRef(0)
  const load = useCallback(({ silent = false } = {}) => {
    const seq = ++loadSeqRef.current
    if (!silent) { setLoading(true); setSlow(false) }
    fetchProgramme(bpId)
      .then(res => {
        if (seq !== loadSeqRef.current) return
        const data = propOr(null, 'data', res.data)
        setProgramme(data)
        setLoading(false)
        onLoadedRef.current?.(data)
      })
      .catch(() => {
        if (seq !== loadSeqRef.current) return
        toast.error("Couldn't load this Building Passport's compliance programme.")
        setLoading(false)
      })
  }, [bpId])

  useEffect(() => { load() }, [load])

  useComplianceChanged(() => load({ silent: true }))

  useEffect(() => {
    if (!targetObligationId || loading) return undefined
    let clearHighlight
    const scroll = setTimeout(() => {
      const el = document.getElementById(`obligation-${targetObligationId}`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightedObligationId(targetObligationId)
      clearHighlight = setTimeout(() => setHighlightedObligationId(null), 2400)
    }, 120)
    return () => { clearTimeout(scroll); clearTimeout(clearHighlight) }
  }, [targetObligationId, loading])

  useEffect(() => {
    if (!loading) { setSlow(false); return }
    const timer = setTimeout(() => setSlow(true), 6000)
    return () => clearTimeout(timer)
  }, [loading])

  const totals = propOr({}, 'totals', programme)
  const canDecide = propOr(false, 'canDecideCompliance', programme)
  const canManage = propOr(false, 'canManageObligations', programme)
  const canRaiseRemedial = propOr(true, 'canRaiseRemedial', programme)
  const canManageTasks = propOr(false, 'canManageTasks', programme)
  const domains = propOr([], 'domains', programme)
  const customiseDomains = useMemo(
    () => domains.map(d => ({ id: d.taskDomainId, name: d.name })).filter(d => d.id),
    [domains]
  )

  const handleCreateTask = (obligation, taskDomainId) => {
    const params = new URLSearchParams({
      title: obligation.name,
      description: `Compliance obligation${obligation.regulatoryFramework ? ` under ${obligation.regulatoryFramework}` : ''}.`,
      bpId,
      source: 'compliance',
      programmeObligationId: obligation.id,
      returnTo: window.location.pathname,
    })
    const domainId = obligation.taskDomainId || taskDomainId
    if (domainId) params.set('taskDomainId', domainId)

    const priority = { low: 'low', medium: 'medium', high: 'high', critical: 'critical' }[obligation.criticality]
    if (priority) params.set('priority', priority)

    const days = obligation.cadenceDays
    const rec = days >= 7 ? { i: Math.floor(days / 7), u: 'week' } : null
    if (rec) {
      params.set('recurrenceType', 'repeating')
      params.set('recurrenceInterval', String(rec.i))
      params.set('recurrenceUnit', rec.u)
      if (obligation.nextDueAt) params.set('recurrenceStartDate', obligation.nextDueAt)
    } else if (obligation.nextDueAt) {
      params.set('deadline', obligation.nextDueAt)
    }

    if (obligation.scope === 'unit') {
      const unitIds = (obligation.unitCoverage?.units || [])
        .map(u => u.unitId || u.unit_id)
        .filter(Boolean)
      if (unitIds.length > 0) {
        params.set('unitIds', unitIds.join(','))
        params.set('unitMode', 'fan_out')
      }
    }

    navigate(`/tasks/create?${params.toString()}`)
  }

  const raiseRemedial = (obligation, unitId = null) => {
    if (remedialBusy) return
    setRemedialBusy(obligation.id)
    const unitLabel = unitId ? (unitNames?.[unitId] ? ` (${unitNames[unitId]})` : '') : ''
    ActionsAPI.create({
      buildingPassportId: bpId,
      programmeObligationId: obligation.id,
      buildingUnitPassportId: unitId || null,
      title: `Remedial. ${obligation.name}${unitLabel}`,
      description: (obligation.description || '').slice(0, 500) || null,
      severity: severityFromWeight(obligation.criticalityWeight ?? 1),
    })
      .then(() => {
        toast.success('Remedial raised. View it on the Remedials tab.')
        refreshRemedialCounts()
      })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not raise remedial.') })
      .finally(() => setRemedialBusy(null))
  }

  const startRaiseRemedial = obligation => {
    if (obligation.scope === 'unit' && (obligation.unitCoverage?.units?.length ?? 0) > 0) {
      ensureUnitNames()
      setRemedialTarget(obligation)
      return
    }
    raiseRemedial(obligation)
  }

  const uploadEvidence = obligation => {
    const typeId = obligation?.fileType?.id
    const q = typeId ? `?method=traditional&type=${encodeURIComponent(typeId)}` : ''
    navigate(`/files/upload/${bpId}${q}`)
  }

  const unitReviewKey = (obligationId, unitId) => `${obligationId}:${unitId}`

  const approveUnitEvidence = (obligation, unit) => {
    const unitId = unit.unitId || unit.unit_id
    const fileId = unit.fileId || unit.file_id
    const key = unitReviewKey(obligation.id, unitId)
    if (unitReviewBusy) return
    setUnitReviewBusy(key)
    reviewObligationEvidence(bpId, obligation.id, { verdict: 'approved', unitId, fileId })
      .then(() => { toast.success('Unit evidence approved.'); notifyComplianceChanged() })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not approve this unit’s evidence.') })
      .finally(() => setUnitReviewBusy(null))
  }

  const denyUnitEvidence = (obligation, unit, reason) => {
    const unitId = unit.unitId || unit.unit_id
    const fileId = unit.fileId || unit.file_id
    const key = unitReviewKey(obligation.id, unitId)
    if (unitReviewBusy) return
    setUnitReviewBusy(key)
    reviewObligationEvidence(bpId, obligation.id, { verdict: 'denied', unitId, fileId, denialReason: reason || null })
      .then(() => { toast.success('Unit evidence denied. This unit is waiting for evidence again.'); notifyComplianceChanged() })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not deny this unit’s evidence.') })
      .finally(() => setUnitReviewBusy(null))
  }

  const approveAllUnits = (obligation) => {
    const units = (obligation.unitCoverage?.units || [])
      .filter(u => (u.state || 'pending') === 'in_review' && (u.fileId || u.file_id))
      .map(u => ({ unitId: u.unitId || u.unit_id, fileId: u.fileId || u.file_id }))
    if (!units.length || unitReviewBusy) return
    const key = unitReviewKey(obligation.id, 'all')
    setUnitReviewBusy(key)
    reviewObligationEvidence(bpId, obligation.id, { verdict: 'approved', units })
      .then(() => { toast.success(`Approved ${units.length} unit${units.length === 1 ? '' : 's'}.`); notifyComplianceChanged() })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not approve these units.') })
      .finally(() => setUnitReviewBusy(null))
  }

  const skip = (obligation, reason) => {
    if (busyObligationId) return
    setBusyObligationId(obligation.id)
    updateProgrammeObligation(bpId, obligation.id, {
      isSkipped: true,
      skipReason: reason || null,
    })
      .then(() => { toast.success('Marked not applicable.'); notifyComplianceChanged() })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not save change.') })
      .finally(() => setBusyObligationId(null))
  }

  const unskip = obligation => {
    if (busyObligationId) return
    setBusyObligationId(obligation.id)
    updateProgrammeObligation(bpId, obligation.id, { isSkipped: false })
      .then(() => { toast.success('Restored.'); notifyComplianceChanged() })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not save change.') })
      .finally(() => setBusyObligationId(null))
  }

  const confirmApplies = obligation => {
    if (busyObligationId) return
    setBusyObligationId(obligation.id)
    updateProgrammeObligation(bpId, obligation.id, { isSkipped: false })
      .then(() => { toast.success('Confirmed. This obligation now counts towards your compliance score.'); notifyComplianceChanged() })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not save change.') })
      .finally(() => setBusyObligationId(null))
  }

  const needsSetupCount = useMemo(
    () => domains.reduce((n, d) => n + (d.obligations || []).filter(isNeedsReview).length, 0),
    [domains]
  )

  const summaryCounts = useMemo(() => ([
    { key: 'needs_setup', label: 'Needs review', value: needsSetupCount, colour: '#475569', Icon: SettingsRoundedIcon },
    { key: 'satisfied', label: 'Satisfied', value: totals.satisfied || 0, ...STATUS_META.satisfied },
    { key: 'in_review', label: 'In review', value: totals.inReview  || 0, ...STATUS_META.in_review },
    { key: 'pending',   label: 'Awaiting',  value: totals.pending   || 0, ...STATUS_META.pending },
    { key: 'expired',   label: 'Expired',   value: totals.expired   || 0, ...STATUS_META.expired },
    { key: 'skipped',   label: 'Skipped',   value: totals.skipped   || 0, ...STATUS_META.skipped },
  ]), [totals, needsSetupCount])

  const readiness = totals.readiness || null
  const ready = readiness ? readiness.ready : true

  const domainOptions = useMemo(
    () => domains.map(d => ({ id: d.taskDomainId || d.name, name: d.name })),
    [domains]
  )

  const totalObligations = useMemo(
    () => domains.reduce((n, d) => n + (d.obligations?.length || 0), 0),
    [domains]
  )

  const filteredDomains = useMemo(() => {
    const q = search.trim().toLowerCase()
    return domains
      .filter(d => domainFilter === 'all' || (d.taskDomainId || d.name) === domainFilter)
      .map(d => {
        const obligations = (d.obligations || []).filter(o => {
          if (statusFilter === 'needs_setup') {
            if (!isNeedsReview(o)) return false
          } else {
            const st = o.isSkipped ? 'skipped' : o.status
            if (st === 'skipped' && statusFilter !== 'skipped') return false
            if (statusFilter && st !== statusFilter) return false
          }
          if (reqFilter !== 'all' && obligationReq(o) !== reqFilter) return false
          if (critFilter !== 'all' && critLevelOf(o) !== critFilter) return false
          if (q) {
            const hay = `${o.name || ''} ${o.regulatoryFramework || ''} ${o.description || ''}`.toLowerCase()
            if (!hay.includes(q)) return false
          }
          return true
        })
        return { ...d, obligations }
      })
      .filter(d => d.obligations.length > 0)
  }, [domains, search, statusFilter, reqFilter, critFilter, domainFilter])

  const firstReviewId = useMemo(
    () => (canManage ? filteredDomains.flatMap(d => d.obligations).find(o => isNeedsReview(o))?.id : undefined),
    [filteredDomains, canManage]
  )

  const visibleCount = useMemo(
    () => filteredDomains.reduce((n, d) => n + d.obligations.length, 0),
    [filteredDomains]
  )

  const kindSplit = useMemo(() => {
    let file = 0, task = 0
    domains.forEach(d => (d.obligations || []).forEach(o => {
      if (o.isSkipped) return
      if (o.obligationKind === 'task') task++; else file++
    }))
    return { file, task }
  }, [domains])

  const scoreHint = useMemo(() => {
    const active = domains.filter(d => (d.obligations || []).some(o => !o.isSkipped))
    if (active.length < 2) return null
    const best = [...active]
      .filter(d => d.score != null)
      .sort((a, b) => b.score - a.score)[0]
    const needs = active
      .filter(d => (d.score ?? 0) < 50)
      .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
    if (needs.length === 0) return null
    const names = needs.map(d => d.name)
    const shown = names.slice(0, 2).join(', ')
    const extra = names.length > 2 ? ` and ${names.length - 2} other area${names.length - 2 > 1 ? 's' : ''}` : ''
    const tail = `${shown}${extra} still need evidence.`
    return best && best.score >= 50
      ? `${best.name} is at ${Math.round(best.score)}%, but ${tail}`
      : `Your score is low because ${tail.charAt(0).toLowerCase()}${tail.slice(1)}`
  }, [domains])

  const filtersActive = !!(search.trim() || statusFilter || domainFilter !== 'all' || critFilter !== 'all' || reqFilter !== 'all')
  const clearFilters = () => {
    setSearch(''); setStatusFilter(null); setDomainFilter('all'); setCritFilter('all'); setReqFilter('all')
  }
  const allCollapsed = filteredDomains.length > 0 && filteredDomains.every(d => collapsed[d.taskDomainId || d.name])
  const toggleAllDomains = () => {
    if (allCollapsed) { setCollapsed({}); return }
    const next = {}
    filteredDomains.forEach(d => { next[d.taskDomainId || d.name] = true })
    setCollapsed(next)
  }

  return (
    <Board $embedded={embedded}>
      <Toolbar>
        {embedded && (
          <ToolbarTitle>
            Compliance tracker
            {!loading && ready && totals.score != null && (
              <ToolbarScore $tint={tintForScore(totals.score)}>{Math.round(totals.score)}%</ToolbarScore>
            )}
            {!loading && !ready && <SettingUpChip>In setup</SettingUpChip>}
          </ToolbarTitle>
        )}
        <ToolbarSpacer />
        {embedded && (
          <OpenFullLink onClick={() => navigate(`/bp-list/${bpId}/programme`)}>
            <LaunchRoundedIcon style={{ fontSize: 15 }} />
            Open full tracker
          </OpenFullLink>
        )}
        <Tooltip title='Show a plain-English guide to what each status means and how your compliance score is worked out.' arrow>
          <HelpLink type='button' onClick={() => setShowHelp(h => !h)}>
            <HelpOutlineRoundedIcon style={{ fontSize: 15 }} /> How this works
          </HelpLink>
        </Tooltip>
        {canDecide && (
          <>
            <Tooltip title='Automatically check the documents already uploaded to this building and match them to the requirements they satisfy, so you don’t upload the same thing twice. Nothing changes until you approve each match.' arrow>
              <MapFilesButton onClick={() => setMapFiles(true)}>
                <AutoAwesomeRoundedIcon style={{ fontSize: 16 }} />
                Match my documents
              </MapFilesButton>
            </Tooltip>
            <AiActionExplainer
              label='How matching works'
              title='How “Match my documents” works'
              lead={(
                <>
                  This checks the documents already uploaded to this building and matches each one to the compliance
                  requirement it satisfies, so you never have to upload the same paperwork twice.
                </>
              )}
              rows={[
                ['What it does', 'It looks at every document already on this building, works out which requirement each one proves, and suggests the matches for you.'],
                ['Nothing changes until you approve', 'Every match is only a suggestion. You review each one and approve it. Nothing is filed, changed or removed on its own.'],
                ['What approving does', 'The document becomes the evidence for that requirement, and the requirement is marked as met. If the document later expires, we remind you.'],
                ['Why bother', 'It finds proof you already hold and puts it against the right requirement, so you’re not chasing or re-uploading documents you already have.'],
              ]}
              tip={(
                <>
                  <strong>Still in control:</strong> you can upload or match documents by hand at any time.
                </>
              )}
            />
          </>
        )}
        {canDecide && (
          <Tooltip title='Add a compliance requirement to track for this building, such as a valid EICR on file or an annual fire-door inspection. Pick one from the standard list or create your own.' arrow>
            <AddObligationButton onClick={() => setAddObligation({ domainId: null })}>
              <AddCircleOutlineRoundedIcon style={{ fontSize: 16 }} />
              Add obligation
            </AddObligationButton>
          </Tooltip>
        )}
      </Toolbar>

      {!loading && ready && totals.score != null && scoreHint && (
        <ScoreHint>
          <HelpOutlineRoundedIcon style={{ fontSize: 15 }} />
          <span>{scoreHint}</span>
        </ScoreHint>
      )}

      {showHelp && (
        <HelpPanel>
          <HelpRow><HelpTerm>Obligation</HelpTerm> A compliance requirement this building must meet, such as a valid EICR on file or an annual fire-door inspection. Each one is met by evidence (a current document) or by completing a task, and together they make up your score.</HelpRow>
          <HelpRow><HelpTerm $c={STATUS_META.satisfied.colour}>Satisfied</HelpTerm> Verified, in-date evidence. Counts fully toward your score.</HelpRow>
          <HelpRow><HelpTerm $c={STATUS_META.in_review.colour}>Awaiting review</HelpTerm> A document is matched but not yet approved (or a periodic certificate has no date). {canDecide
            ? <>It doesn’t count until you <ReviewJump type='button' onClick={() => navigate(`/bp-list/${bpId}/compliance#review`)}>approve it on the Overview tab</ReviewJump>.</>
            : <>It doesn’t count until the Building Passport owner approves it.</>}</HelpRow>
          <HelpRow><HelpTerm $c={STATUS_META.pending.colour}>Awaiting evidence</HelpTerm> Nothing uploaded or completed yet.</HelpRow>
          <HelpRow><HelpTerm $c={STATUS_META.expired.colour}>Expired</HelpTerm> Evidence existed but is past its date.</HelpRow>
          <HelpRow><HelpTerm $c={STATUS_META.skipped.colour}>Not applicable</HelpTerm> Marked as not relevant to this building.</HelpRow>
          <HelpRow><HelpTerm>Requirement</HelpTerm> Whole-building file = one document for the building; Per-unit file = a document on each applicable unit; Task = a completed task or inspection record.</HelpRow>
          <HelpRow><HelpTerm>Score</HelpTerm> The share of applicable obligations that are satisfied, weighted by criticality. Satisfied evidence counts its full weight; evidence awaiting review doesn’t count until it’s approved; awaiting-evidence and expired obligations add nothing until fixed; per-unit obligations count their coverage fraction.</HelpRow>
        </HelpPanel>
      )}

      {!loading && (
        <OnceCallout
          storageKey='bp_compliance_model_explained'
          tone='info'
          title='New here? How compliance works'
          actions={<TasksRemedialsExplainer align='left' label='See how tasks & remedials differ' />}
        >
          Each row below is an <strong>obligation</strong>: something this building must do. You meet it
          by uploading a <strong>file</strong> (a document) or completing a <strong>task</strong> (planned
          work). Each row shows which. When something’s wrong, you raise a <strong>remedial</strong> to
          fix it. Your <strong>score</strong> is the share of obligations currently met, weighted by
          criticality.
          <ModelDiagramWrap><ConceptModelDiagram /></ModelDiagramWrap>
        </OnceCallout>
      )}

      {!loading && !ready && (() => {
        const steps = (readiness?.steps || []).filter(s => s.status !== 'na' && s.key !== 'ai_prefill' && s.key !== 'key_safety_roles')
        const doneCount = steps.filter(s => s.status === 'done').length
        const incomplete = steps.filter(s => s.status !== 'done')
        const stepDestinations = {
          review_obligations: `/bp-list/${bpId}/compliance/setup`,
          assign_units: `/bp-list/${bpId}/compliance/setup`,
          tracking_active: `/tasks/create?bpId=${bpId}`,
        }
        const destinationFor = s =>
          s.key === 'tracking_active' && s.status === 'done'
            ? '/tasks'
            : (stepDestinations[s.key] || `/bp-list/${bpId}/compliance/setup`)
        if (steps.length === 0) {
          return (
            <ReadinessBanner>
              <ReadinessHead>Unlock your compliance score</ReadinessHead>
              <ReadinessBody>
                The score stays hidden until <strong>setup</strong> is complete. Give each
                obligation a task, a document, or a not-applicable decision to unlock it.
              </ReadinessBody>
              <ComplianceSetupExplainer align='left' />
              <ReadinessGoals>
                <Goal as='button' type='button' onClick={() => navigate(`/bp-list/${bpId}/compliance/setup`)}>
                  Open obligation list →
                </Goal>
              </ReadinessGoals>
            </ReadinessBanner>
          )
        }
        return (
          <ReadinessBanner>
            <ReadinessHead>Unlock your compliance score</ReadinessHead>
            <ReadinessBody>
              The score stays hidden until <strong>setup</strong> is complete. It would read
              artificially low otherwise. Finish setup to unlock the score:{' '}
              <strong>{doneCount} of {steps.length}</strong> done.
              {incomplete.length > 0 && <> Still to do: {incomplete.map(s => s.label).join(' · ')}.</>}
            </ReadinessBody>
            <ComplianceSetupExplainer align='left' />
            <ReadinessGoals>
              {steps.map(s => (
                <Goal
                  key={s.key}
                  as='button'
                  type='button'
                  $done={s.status === 'done'}
                  onClick={() => navigate(destinationFor(s))}
                >
                  {s.label}{s.detail ? ` · ${s.detail}` : ''} →
                </Goal>
              ))}
            </ReadinessGoals>
          </ReadinessBanner>
        )
      })()}

      {!loading && (() => {
        const rolesStep = (readiness?.steps || []).find(s => s.key === 'key_safety_roles')
        if (!rolesStep || rolesStep.status === 'done' || !Array.isArray(rolesStep.roles)) return null
        return (
          <SafetyRolesSetup
            bpId={bpId}
            roles={rolesStep.roles}
            onChanged={() => notifyComplianceChanged()}
          />
        )
      })()}

      {!loading && (
        <ProgrammeSubtitle>
          {totalObligations} obligations · curated against UK Building Safety regulation
        </ProgrammeSubtitle>
      )}

      {!loading && (kindSplit.file + kindSplit.task) > 0 && (
        <KindSplitLine title='How each obligation is met. “File” obligations are satisfied by uploading a matching document; “Task” obligations need a recurring task or inspection completed. Uploading a file will not satisfy them.'>
          <KindSplitItem $tint='#0e7490'><strong>{kindSplit.file}</strong> met by uploading a file</KindSplitItem>
          <KindSplitDot>·</KindSplitDot>
          <KindSplitItem $tint='#c2410c'><strong>{kindSplit.task}</strong> need a task completed</KindSplitItem>
        </KindSplitLine>
      )}

      <SummaryRow>
        {summaryCounts.map(s => {
          const SIcon = s.Icon
          const active = statusFilter === s.key
          return (
            <SummaryChip
              key={s.key}
              type='button'
              $colour={s.colour}
              $active={active}
              onClick={() => setStatusFilter(active ? null : s.key)}
              title={`Show only ${s.label.toLowerCase()}`}
            >
              <SummaryChipIcon $colour={s.colour}><SIcon style={{ fontSize: 16 }} /></SummaryChipIcon>
              <SummaryChipText>
                <SummaryChipValue>{s.value}</SummaryChipValue>
                <SummaryChipLabel>{s.label}</SummaryChipLabel>
              </SummaryChipText>
            </SummaryChip>
          )
        })}
      </SummaryRow>

      {statusFilter === 'needs_setup' && (
        <ReviewFilterBanner>
          <ReviewFilterIcon><SettingsRoundedIcon style={{ fontSize: 18 }} /></ReviewFilterIcon>
          <ReviewFilterBody>
            <ReviewFilterTitle>These obligations haven’t been reviewed yet</ReviewFilterTitle>
            <ReviewFilterText>
              For each one, decide whether it applies to your building: press <strong>“Yes, this applies”</strong>{' '}
              to keep tracking it in your compliance score, or <strong>“Not applicable”</strong> to exclude it.
              You don’t need to upload a document to finish. That’s part of the score, not setup.
            </ReviewFilterText>
          </ReviewFilterBody>
          <ComplianceSetupExplainer align='left' label='What does this mean?' />
        </ReviewFilterBanner>
      )}

      {!loading && domains.length > 0 && (
        <Controls>
          <SearchWrap>
            <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
            <SearchInput placeholder='Search obligations…' value={search} onChange={e => setSearch(e.target.value)} />
            {search && <ClearSearch onClick={() => setSearch('')}>×</ClearSearch>}
          </SearchWrap>
          <SearchableSelect
            value={domainFilter}
            onChange={v => setDomainFilter(v)}
            options={[{ value: 'all', label: 'All domains' }, ...domainOptions.map(d => ({ value: d.id, label: d.name }))]}
            ariaLabel='Filter by domain'
          />
          <SearchableSelect
            value={reqFilter}
            onChange={v => setReqFilter(v)}
            options={REQ_OPTIONS}
            ariaLabel='Filter by requirement type'
          />
          <SearchableSelect
            value={critFilter}
            onChange={v => setCritFilter(v)}
            options={CRIT_OPTIONS}
            ariaLabel='Filter by criticality'
          />
          <ControlSpacer />
          <ResultCount>Showing {visibleCount} of {totalObligations}</ResultCount>
          {filtersActive && <ClearLink onClick={clearFilters}>Clear</ClearLink>}
          <CollapseAll onClick={toggleAllDomains}>{allCollapsed ? 'Expand all' : 'Collapse all'}</CollapseAll>
        </Controls>
      )}

      {loading && (
        <SkeletonWrap>
          {[0, 1].map(i => (
            <SkeletonSection key={i} aria-hidden='true'>
              <SkeletonHeader>
                <SkeletonBlock $w='40px' $h='40px' $r='12px' />
                <SkeletonStack>
                  <SkeletonBlock $w='180px' $h='16px' />
                  <SkeletonBlock $w='120px' $h='10px' />
                </SkeletonStack>
                <SkeletonBlock $w='52px' $h='24px' $r='6px' />
              </SkeletonHeader>
              <SkeletonCards>
                {[0, 1, 2].map(j => (
                  <SkeletonCard key={j}>
                    <SkeletonBlock $w='72%' $h='13px' />
                    <SkeletonBlock $w='46%' $h='10px' />
                    <SkeletonBlock $w='100%' $h='36px' $r='10px' />
                    <SkeletonBlock $w='58%' $h='10px' />
                  </SkeletonCard>
                ))}
              </SkeletonCards>
            </SkeletonSection>
          ))}
          {slow && (
            <SlowRetry>
              <SlowText>This is taking longer than usual. The connection may have dropped.</SlowText>
              <RetryButton onClick={() => load()}>Retry</RetryButton>
            </SlowRetry>
          )}
        </SkeletonWrap>
      )}

      {!loading && domains.length === 0 && (
        <EmptyState>
          No obligations apply to this Building Passport yet. This usually means the Building Passport profile is incomplete (no jurisdiction, height, or usage set). Set those and the programme will rebuild.
        </EmptyState>
      )}

      {!loading && domains.length > 0 && filteredDomains.length === 0 && (
        <EmptyState>
          No obligations match your filters. <InlineLink onClick={clearFilters}>Clear filters</InlineLink>
        </EmptyState>
      )}

      {!loading && filteredDomains.map((dom, di) => {
        const DIcon = DOMAIN_ICONS[dom.icon] || ShieldRoundedIcon
        const tone = dom.colour || '#33becc'
        const key = dom.taskDomainId || dom.name
        const isCollapsed = !!collapsed[key]
        const satisfiedCount = dom.obligations.filter(o => o.status === 'satisfied').length
        const totalActive = dom.obligations.filter(o => !o.isSkipped).length
        const domainScore = dom.score != null
          ? Math.round(dom.score)
          : (totalActive > 0 ? Math.round((satisfiedCount / totalActive) * 100) : null)
        return (
          <DomainSection key={key} $stagger={di}>
            <DomainHeader $tone={tone} $collapsed={isCollapsed} onClick={() => setCollapsed(p => ({ ...p, [key]: !isCollapsed }))}>
              <DomainIconChip $tone={tone}><DIcon style={{ fontSize: 24 }} /></DomainIconChip>
              <DomainHeaderText>
                <DomainName>{dom.name}</DomainName>
                <DomainMeta>{dom.obligations.length} shown · {satisfiedCount} of {totalActive} satisfied</DomainMeta>
              </DomainHeaderText>
              {canDecide && (
                <DomainAddBtn
                  onClick={e => { e.stopPropagation(); setAddObligation({ domainId: dom.taskDomainId || null }) }}
                  title={`Add an obligation to ${dom.name}`}
                >
                  <AddCircleOutlineRoundedIcon style={{ fontSize: 15 }} /> Add
                </DomainAddBtn>
              )}
              <DomainScore $tint={tintForScore(domainScore)}>{domainScore === null ? 'No score' : `${domainScore}%`}</DomainScore>
              <DomainCaret $open={!isCollapsed}>›</DomainCaret>
            </DomainHeader>

            {!isCollapsed && (
              <Cards>
                {dom.obligations.map((obl, oi) => (
                  <ObligationCard
                    key={obl.id}
                    domId={`obligation-${obl.id}`}
                    $highlight={obl.id === highlightedObligationId}
                    obligation={obl}
                    tourAnchor={obl.id === firstReviewId}
                    $stagger={oi}
                    busy={busyObligationId === obl.id}
                    raiseBusy={remedialBusy === obl.id}
                    canDecide={canDecide}
                    canManage={canManage}
                    canRaiseRemedial={canRaiseRemedial}
                    canManageTasks={canManageTasks}
                    onSkip={() => setSkipTarget(obl)}
                    onUnskip={() => unskip(obl)}
                    onConfirmApplies={() => confirmApplies(obl)}
                    onFlag={() => setFlagTarget(obl)}
                    onCreateTask={() => handleCreateTask(obl, dom.taskDomainId)}
                    onViewTask={task => navigate(`/tasks/${task.id}?from=programme&bpId=${bpId}`)}
                    onTaskUpdated={() => notifyComplianceChanged()}
                    onRaiseRemedial={() => startRaiseRemedial(obl)}
                    onRaiseUnitRemedial={unit => raiseRemedial(obl, unit.unitId || unit.unit_id)}
                    onUploadEvidence={() => uploadEvidence(obl)}
                    onOpenUnits={() => setPerUnitTarget(obl)}
                    onLinkFile={() => setLinkTarget(obl)}
                    onMarkDone={() => setLogTarget(obl)}
                    onHistory={() => setHistoryTarget(obl)}
                    onCustomise={() => setCustomiseTarget(obl)}
                    bpId={bpId}
                    onScopeChanged={() => notifyComplianceChanged()}
                    unitNames={unitNames}
                    onEnsureUnitNames={ensureUnitNames}
                    unitReviewBusy={unitReviewBusy}
                    unitReviewKey={unitReviewKey}
                    onApproveUnit={approveUnitEvidence}
                    onApproveAllUnits={approveAllUnits}
                    onDenyUnit={(obl, unit) => { setUnitDenyTarget({ obligation: obl, unit }); setUnitDenyReason('') }}
                  />
                ))}
              </Cards>
            )}
          </DomainSection>
        )
      })}

      {skipTarget && createPortal(
        <ModalBackdrop onClick={() => setSkipTarget(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>Mark as not applicable</ModalTitle>
            <ModalBody>
              <strong>{skipTarget.name}</strong> will be removed from this
              Building Passport's programme score. You can give a brief reason so
              future reviewers know why.
            </ModalBody>
            <ReasonInput
              autoFocus
              placeholder='e.g. "No gas supply on site"'
              value={skipReason}
              onChange={e => setSkipReason(e.target.value)}
              maxLength={1000}
              data-tour-id='programme.na-reason'
            />
            <ModalActions>
              <SecondaryAction onClick={() => setSkipTarget(null)}>Cancel</SecondaryAction>
              <PrimaryAction onClick={() => {
                skip(skipTarget, skipReason.trim())
                setSkipTarget(null); setSkipReason('')
              }} data-tour-id='programme.na-confirm'>
                Mark not applicable
              </PrimaryAction>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>,
        document.body
      )}

      {flagTarget && (
        <FlagObligationModal
          obligation={flagTarget}
          bpId={bpId}
          onClose={() => setFlagTarget(null)}
        />
      )}

      {addObligation && (
        <AddProgrammeObligationModal
          bpId={bpId}
          defaultDomainId={addObligation.domainId}
          onClose={() => setAddObligation(null)}
          onAdded={() => notifyComplianceChanged()}
        />
      )}

      {linkTarget && (
        <LinkEvidenceModal
          bpId={bpId}
          obligation={linkTarget}
          onClose={() => setLinkTarget(null)}
          onLinked={() => { setLinkTarget(null); notifyComplianceChanged() }}
        />
      )}

      {perUnitTarget && (
        <PerUnitEvidenceModal
          bpId={bpId}
          obligation={perUnitTarget}
          onClose={() => setPerUnitTarget(null)}
          onChanged={() => notifyComplianceChanged()}
        />
      )}

      {historyTarget && (
        <ObligationHistoryModal
          bpId={bpId}
          obligation={historyTarget}
          canRecord={canDecide}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {customiseTarget && (
        <CustomiseObligationModal
          bpId={bpId}
          bpName={programme?.buildingPassport?.name}
          obligation={customiseTarget}
          availableDomains={customiseDomains}
          onClose={() => setCustomiseTarget(null)}
          onSaved={() => { setCustomiseTarget(null); notifyComplianceChanged() }}
        />
      )}

      {logTarget && (
        <LogCompletionModal
          bpId={bpId}
          obligation={logTarget}
          onClose={() => setLogTarget(null)}
          onLogged={() => { setLogTarget(null); notifyComplianceChanged() }}
        />
      )}

      {mapFiles && (
        <MapExistingFilesModal
          bpId={bpId}
          onClose={() => setMapFiles(false)}
          onApplied={() => { setMapFiles(false); notifyComplianceChanged() }}
        />
      )}

      {unitDenyTarget && createPortal(
        <ModalBackdrop onClick={() => setUnitDenyTarget(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>Deny unit evidence</ModalTitle>
            <ModalBody>
              The current document for <strong>{unitNames?.[unitDenyTarget.unit.unitId || unitDenyTarget.unit.unit_id] || 'this unit'}</strong> will
              stop counting towards <strong>{unitDenyTarget.obligation.name}</strong>, and the unit returns to awaiting evidence.
              Only this unit is affected. You can give a brief reason.
            </ModalBody>
            <ReasonInput
              autoFocus
              placeholder='e.g. "Certificate covers the wrong flat"'
              value={unitDenyReason}
              onChange={e => setUnitDenyReason(e.target.value)}
              maxLength={1000}
            />
            <ModalActions>
              <SecondaryAction onClick={() => setUnitDenyTarget(null)}>Cancel</SecondaryAction>
              <PrimaryAction onClick={() => {
                denyUnitEvidence(unitDenyTarget.obligation, unitDenyTarget.unit, unitDenyReason.trim())
                setUnitDenyTarget(null); setUnitDenyReason('')
              }}>
                Deny evidence
              </PrimaryAction>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>,
        document.body
      )}

      {remedialTarget && (
        <RaiseUnitRemedialModal
          obligation={remedialTarget}
          unitNames={unitNames}
          busy={remedialBusy === remedialTarget.id}
          onClose={() => setRemedialTarget(null)}
          onRaise={unitId => { raiseRemedial(remedialTarget, unitId); setRemedialTarget(null) }}
        />
      )}
    </Board>
  )
}

const RaiseUnitRemedialModal = ({ obligation, unitNames, busy, onClose, onRaise }) => {
  const [unitId, setUnitId] = useState('')
  const units = obligation.unitCoverage?.units || []
  return createPortal(
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalTitle>Raise remedial</ModalTitle>
        <ModalBody>
          <strong>{obligation.name}</strong> applies to each unit. You can raise this remedial
          for the whole obligation, or scope it to a single unit. Resolving a unit-scoped
          remedial satisfies only that unit.
        </ModalBody>
        <UnitPickerLabel>Which unit does this concern?</UnitPickerLabel>
        <SearchableSelect
          value={unitId}
          onChange={v => setUnitId(v || '')}
          options={units.map(u => {
            const uid = u.unitId || u.unit_id
            return { value: uid, label: unitNames?.[uid] || 'Unit' }
          })}
          clearable
          clearLabel='Whole obligation (no specific unit)'
          placeholder='Whole obligation (no specific unit)'
          ariaLabel='Which unit does this concern?'
        />
        <ModalActions>
          <SecondaryAction onClick={onClose} disabled={busy}>Cancel</SecondaryAction>
          <PrimaryAction disabled={busy} onClick={() => onRaise(unitId || null)}>
            {busy ? 'Raising…' : 'Raise remedial'}
          </PrimaryAction>
        </ModalActions>
      </ModalCard>
    </ModalBackdrop>,
    document.body
  )
}

const HintTarget = styled.span`
  display: inline-flex;
`

const Hint = ({ title, children }) => (
  <Tooltip
    title={title}
    arrow
    placement='top'
    enterDelay={200}
    slotProps={{
      popper: { sx: { zIndex: 100060 } },
      tooltip: { sx: { maxWidth: 300, fontSize: '12px', lineHeight: 1.45, padding: '8px 11px', bgcolor: '#1d2433', fontWeight: 500 } },
      arrow: { sx: { color: '#1d2433' } },
    }}
  >
    <HintTarget>{children}</HintTarget>
  </Tooltip>
)

const ObligationCard = ({ domId, $highlight, obligation, tourAnchor, bpId, busy, raiseBusy, canDecide, canManage, canRaiseRemedial, canManageTasks, onSkip, onUnskip, onConfirmApplies, onFlag, onCreateTask, onViewTask, onTaskUpdated, onRaiseRemedial, onRaiseUnitRemedial, onUploadEvidence, onOpenUnits, onLinkFile, onMarkDone, onHistory, onCustomise, onScopeChanged, unitNames, onEnsureUnitNames, unitReviewBusy, unitReviewKey, onApproveUnit, onApproveAllUnits, onDenyUnit, $stagger }) => {
  const [showUnits, setShowUnits] = useState(false)
  const [unitSearch, setUnitSearch] = useState('')
  const [unitLimit, setUnitLimit] = useState(25)
  const [quickEditOpen, setQuickEditOpen] = useState(false)
  const status = obligation.isSkipped ? 'skipped' : obligation.status
  const meta = STATUS_META[status] || STATUS_META.pending
  const SIcon = meta.Icon
  const isFile = obligation.obligationKind === 'file_type'
  const isUnit = obligation.scope === 'unit'
  const needsReview = isNeedsReview(obligation)
  const canRaise = canRaiseRemedial !== false
  const coverage = obligation.unitCoverage || null
  const coveragePct = coverage && coverage.applicable > 0
    ? Math.round((coverage.covered / coverage.applicable) * 100)
    : 0
  const inReviewCount = coverage
    ? (coverage.inReview ?? (coverage.units || []).filter(u => (u.state || 'pending') === 'in_review').length)
    : 0
  const reqLabel = !isFile
    ? (isUnit
        ? (coverage && coverage.applicable ? `Per-unit task · ${coverage.covered}/${coverage.applicable} units` : 'Per-unit task')
        : 'Task')
    : isUnit
      ? (coverage && coverage.applicable ? `Per-unit file · ${coverage.covered}/${coverage.applicable} units` : 'Per-unit file')
      : 'Whole-building file'
  const reqTone = !isFile ? '#c2410c' : isUnit ? '#7C5FE0' : '#0e7490'
  const coveredLabel = isFile ? 'Covered' : 'Done'
  const expiredLabel = isFile ? 'Expired' : 'Overdue'
  const pendingLabel = isFile ? 'Awaiting document' : 'Awaiting completion'
  const unitStateLabel = state =>
    state === 'covered' ? coveredLabel
      : state === 'expired' ? expiredLabel
        : state === 'in_review' ? 'Awaiting review'
          : state === 'denied' ? 'Denied'
            : pendingLabel
  const isSystem = obligation.isSystem !== false
  const lt = obligation.legislationType ? legislationTypeMeta(obligation.legislationType) : null
  const allUnits = coverage?.units || []
  const filteredUnits = unitSearch.trim()
    ? allUnits.filter(u => (unitNames?.[u.unitId || u.unit_id] || 'Unit').toLowerCase().includes(unitSearch.trim().toLowerCase()))
    : allUnits
  const visibleUnits = filteredUnits.slice(0, unitLimit)

  return (
    <Card id={domId} $highlight={$highlight} $stagger={$stagger} $colour={meta.colour} $skipped={obligation.isSkipped}>
      <CardHeader>
        <CardTitle>{obligation.name}</CardTitle>
        <StatusBadge $colour={meta.colour}>
          <SIcon style={{ fontSize: 14 }} />
          <span>{meta.label}</span>
        </StatusBadge>
      </CardHeader>

      <MetaLine>
        {lt && <LegTypeBadge $fg={lt.fg} $bg={lt.bg} title={lt.desc}>{lt.label}</LegTypeBadge>}
        <MetaChip
          $tint={criticalityTint(obligation.criticalityWeight)}
          title={obligation.criticalityOverridden ? 'Criticality overridden for this building. This value is what counts in the score.' : 'Criticality'}
        >
          {obligation.criticality
            ? obligation.criticality.charAt(0).toUpperCase() + obligation.criticality.slice(1)
            : criticalityLabel(obligation.criticalityWeight)}
          {obligation.criticalityOverridden ? ' · override' : ''}
        </MetaChip>
        {obligation.cadenceDays && <MetaChip>{cadenceLabel(obligation.cadenceDays)}</MetaChip>}
        {obligation.nextDueAt && !obligation.isSkipped && (
          <MetaChip $tint={obligation.status === 'expired' ? '#b00020' : undefined}>
            {obligation.status === 'satisfied' ? 'Renew ' : 'Due '}{formatDate(obligation.nextDueAt)}
          </MetaChip>
        )}
        {obligation.penaltySeverity && PENALTY_SEVERITIES[obligation.penaltySeverity] && (
          <MetaChip $tint={PENALTY_SEVERITIES[obligation.penaltySeverity].colour} title='Penalty severity if this obligation is breached'>
            Penalty: {PENALTY_SEVERITIES[obligation.penaltySeverity].label}
          </MetaChip>
        )}
      </MetaLine>

      <ReqLine>
        <ReqLabel>Requirement</ReqLabel>
        <ObligationScopePill
          bpId={bpId}
          obligation={obligation}
          canEdit={canManage}
          kind={isFile ? 'file' : 'task'}
          onChanged={onScopeChanged}
        />
      </ReqLine>

      {obligation.description && <Description>{obligation.description}</Description>}

      {obligation.isSkipped && obligation.skipReason && (
        <SkipReason>
          <BlockRoundedIcon style={{ fontSize: 14 }} />
          <span>
            {obligation.skipReason}
            {obligation.aiPrefillConfidence != null && ` · AI ${Math.round(obligation.aiPrefillConfidence * 100)}% confident`}
          </span>
        </SkipReason>
      )}

      {obligation.evidence && (
        <EvidenceRow>
          {obligation.evidence.kind === 'file' && obligation.evidence.thumbnailUrl ? (
            <EvidenceThumb src={obligation.evidence.thumbnailUrl} alt='' />
          ) : (
            <EvidenceIcon>
              {obligation.evidence.kind === 'file'
                ? <InsertDriveFileRoundedIcon style={{ fontSize: 18 }} />
                : <AssignmentTurnedInRoundedIcon style={{ fontSize: 18 }} />}
            </EvidenceIcon>
          )}
          <EvidenceText>
            <EvidenceLabel>{status === 'in_review'
              ? (obligation.inReviewReason === 'needs_date' ? 'Submitted. Needs a date' : 'Submitted. Needs approval')
              : status === 'expired' ? 'Expired evidence' : 'Satisfied by'}</EvidenceLabel>
            <EvidenceName>
              {obligation.evidence.kind === 'file'
                ? obligation.evidence.name
                : obligation.evidence.title}
            </EvidenceName>
            {obligation.evidence.kind === 'file' && obligation.evidence.expiresAt && (
              <EvidenceSub>Valid until {formatDate(obligation.evidence.expiresAt)}</EvidenceSub>
            )}
            {status === 'in_review' && obligation.inReviewReason === 'needs_date' && (
              <EvidenceSub>This periodic certificate has no date. Add an expiry or inspection date for it to count.</EvidenceSub>
            )}
            {obligation.evidence.kind === 'task' && obligation.evidence.completedAt && (
              <EvidenceSub>Completed {formatDate(obligation.evidence.completedAt)}</EvidenceSub>
            )}
            {obligation.satisfiedSource && (
              <SourceBadge
                $manual={obligation.satisfiedSource === 'manual'}
                title={obligation.satisfiedSource === 'manual'
                  ? 'Someone recorded this as done. It was not matched from an uploaded document or a completed task'
                  : obligation.obligationKind === 'task'
                    ? 'Satisfied automatically by a completed task in this domain'
                    : 'Satisfied automatically. Guru matched an uploaded document to this obligation'}
              >
                {obligation.satisfiedSource === 'manual'
                  ? 'Manually recorded'
                  : obligation.obligationKind === 'task'
                    ? 'From a completed task'
                    : 'Identified by Guru'}
              </SourceBadge>
            )}
          </EvidenceText>
        </EvidenceRow>
      )}

      {isUnit && coverage && (
        <UnitCoverageBox>
          <UnitCoverageHead>
            <UnitCoverageHeadLeft>
              <UnitCoverageLabel>Per-unit coverage</UnitCoverageLabel>
              <PerUnitSatisfyExplainer taskKind={!isFile} />
            </UnitCoverageHeadLeft>
            <UnitCoverageCount>{coverage.covered}/{coverage.applicable} units</UnitCoverageCount>
          </UnitCoverageHead>
          <UnitTrack>
            <UnitFill style={{ width: `${coveragePct}%` }} $tint={meta.colour} />
          </UnitTrack>
          <UnitCoverageMeta>
            {coverage.covered} covered · {coverage.pending} awaiting{inReviewCount > 0 ? ` · ${inReviewCount} in review` : ''}{coverage.expired > 0 ? ` · ${coverage.expired} expired` : ''}
            {isFile && inReviewCount > 0
              ? (canDecide
                ? <>. {coveragePct}% of units covered. Approve the {inReviewCount} matched below to count them; this obligation is fully satisfied once all {coverage.applicable} units are approved.</>
                : <>. {coveragePct}% of units covered. It’s fully satisfied once the Building Passport owner approves each unit’s document.</>)
              : `. ${coveragePct}% of units covered.`}
          </UnitCoverageMeta>
          {isFile && canDecide && inReviewCount > 0 && (
            <ApproveAllUnitsBtn
              type='button'
              disabled={unitReviewBusy === unitReviewKey?.(obligation.id, 'all')}
              title='Approve every unit whose document is matched and awaiting review. Each counts towards the score straight away.'
              onClick={() => onApproveAllUnits?.(obligation)}
            >
              <VerifiedRoundedIcon style={{ fontSize: 14 }} /> Approve all {inReviewCount} awaiting
            </ApproveAllUnitsBtn>
          )}
          {(coverage.units?.length ?? 0) > 0 && (
            <>
              <UnitListToggle type='button' onClick={() => { onEnsureUnitNames?.(); setShowUnits(s => !s) }}>
                {showUnits ? 'Hide units' : `View ${coverage.units.length} units`}
              </UnitListToggle>
              {showUnits && (
                <>
                  {coverage.units.length > 10 && (
                    <UnitSearch
                      value={unitSearch}
                      onChange={e => { setUnitSearch(e.target.value); setUnitLimit(25) }}
                      placeholder='Search units…' />
                  )}
                  <UnitList onScroll={e => {
                    const el = e.currentTarget
                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                      setUnitLimit(l => (l < filteredUnits.length ? l + 25 : l))
                    }
                  }}>
                    {visibleUnits.map(u => {
                      const uid = u.unitId || u.unit_id
                      const state = u.state || 'pending'
                      const hasFile = !!(u.fileId || u.file_id)
                      const rowBusy = unitReviewBusy === unitReviewKey?.(obligation.id, uid)
                      const canReview = isFile && canDecide
                      return (
                        <UnitListRow key={uid}>
                          <UnitListName>{unitNames?.[uid] || 'Unit'}</UnitListName>
                          <UnitStateChip $state={state}>
                            {unitStateLabel(state)}
                          </UnitStateChip>
                          {(u.expiryDate || u.expiry_date) && (
                            <UnitListExpiry>until {formatDate(u.expiryDate || u.expiry_date)}</UnitListExpiry>
                          )}
                          {canReview && state === 'in_review' && hasFile && (
                            <UnitReviewBtns>
                              <UnitReviewBtn
                                $tone='ok'
                                disabled={rowBusy}
                                title='Approve this unit’s document. It counts towards the score straight away. Only this unit is affected.'
                                onClick={() => onApproveUnit?.(obligation, u)}
                              >
                                <VerifiedRoundedIcon style={{ fontSize: 12 }} /> Approve
                              </UnitReviewBtn>
                              <UnitReviewBtn
                                $tone='bad'
                                disabled={rowBusy}
                                title='Deny this unit’s document. It stops counting and the unit returns to awaiting evidence.'
                                onClick={() => onDenyUnit?.(obligation, u)}
                              >
                                <BlockRoundedIcon style={{ fontSize: 12 }} /> Deny
                              </UnitReviewBtn>
                            </UnitReviewBtns>
                          )}
                          {canReview && state === 'denied' && hasFile && (
                            <UnitReviewBtns>
                              <UnitReviewBtn
                                $tone='ok'
                                disabled={rowBusy}
                                title='Re-review. Approve the current document for this unit after all.'
                                onClick={() => onApproveUnit?.(obligation, u)}
                              >
                                <ReplayRoundedIcon style={{ fontSize: 12 }} /> Re-review
                              </UnitReviewBtn>
                            </UnitReviewBtns>
                          )}
                          {canRaise && onRaiseUnitRemedial && (state === 'expired' || state === 'denied') && (
                            <UnitRemedialBtn
                              disabled={raiseBusy}
                              title='Raise a remedial for this unit only.'
                              onClick={() => onRaiseUnitRemedial(u)}
                            >
                              <BuildRoundedIcon style={{ fontSize: 12 }} /> Remedial
                            </UnitRemedialBtn>
                          )}
                        </UnitListRow>
                      )
                    })}
                  </UnitList>
                  <UnitListMeta>
                    Showing {visibleUnits.length} of {filteredUnits.length}
                    {filteredUnits.length !== allUnits.length ? ` (filtered from ${allUnits.length})` : ''}
                  </UnitListMeta>
                </>
              )}
            </>
          )}
        </UnitCoverageBox>
      )}

      {!obligation.evidence && !obligation.isSkipped && !isUnit && (
        <PendingHint>
          {isFile
            ? `Upload a file of type "${obligation.fileType?.name || obligation.name}" for the whole building to satisfy this obligation.`
            : `Complete a task in this domain for the whole building to satisfy this obligation.`}
        </PendingHint>
      )}

      <ObligationProvenance
        cadenceBasis={obligation.cadenceBasis}
        lastVerifiedAt={obligation.lastVerifiedAt}
        reviewDueAt={obligation.reviewDueAt}
        isSystem={obligation.isSystem}
        citationUrl={obligation.citationUrl}
        regulatoryFramework={obligation.regulatoryFramework}
        onHistory={onHistory}
      />

      {needsReview && canManage && (
        <ReviewPrompt data-tour-id={tourAnchor ? 'programme.review-prompt' : undefined}>
          <ReviewText>
            <strong>Not reviewed yet.</strong> Does this obligation apply to your building? You don’t need a document to decide. That’s tracked by your compliance score.
          </ReviewText>
          <ReviewBtns>
            <Hint title='Confirm this applies. It’s added to your compliance score to be satisfied later. You can upload a document or schedule a task any time.'>
              <ConfirmBtn disabled={busy} onClick={onConfirmApplies}>
                <CheckCircleRoundedIcon style={{ fontSize: 15 }} /> Yes, this applies
              </ConfirmBtn>
            </Hint>
            <Hint title='This obligation doesn’t apply to this building. It stops counting towards the score (e.g. no gas supply, no lift).'>
              <ReviewNaBtn disabled={busy} onClick={onSkip}>
                <BlockRoundedIcon style={{ fontSize: 15 }} /> Not applicable
              </ReviewNaBtn>
            </Hint>
          </ReviewBtns>
        </ReviewPrompt>
      )}

      {obligation.isSkipped ? (
        canDecide && (
          <Actions>
            <Hint title='Restore this obligation. Put it back on the tracker and start scoring it again.'>
              <ActBtn disabled={busy} onClick={onUnskip}>
                <ReplayRoundedIcon style={{ fontSize: 15 }} /> Restore
              </ActBtn>
            </Hint>
          </Actions>
        )
      ) : (
        <Actions>
          {isFile ? (
            isUnit ? (
              <Hint title='Add evidence for each unit. Per-unit evidence lives on the unit, not the building.'>
                <ActBtn $primary onClick={onOpenUnits}>
                  <UploadFileRoundedIcon style={{ fontSize: 15 }} /> Add unit evidence
                </ActBtn>
              </Hint>
            ) : (
              <Hint title='Upload a document of the matching type. For a document obligation the file itself is the evidence that satisfies it.'>
                <ActBtn $primary onClick={onUploadEvidence} data-tour-id='programme.upload-evidence-btn'>
                  <UploadFileRoundedIcon style={{ fontSize: 15 }} /> Upload evidence
                </ActBtn>
              </Hint>
            )
          ) : obligation.openTask ? (
            <>
              <Hint title='A task for this obligation is already scheduled. Open it to see or reassign the work.'>
                <ActBtn $primary onClick={() => onViewTask(obligation.openTask)}>
                  <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> View task{obligation.openTask.deadline ? ` · due ${formatDate(obligation.openTask.deadline)}` : ''}
                </ActBtn>
              </Hint>
              {obligation.openTask.assignees?.length > 0 && (
                <AssigneeAvatars assignees={obligation.openTask.assignees} />
              )}
              {canManageTasks && (
                <Hint title='Quickly assign people, an approver or a contractor and set the due date, without opening the full task page.'>
                  <ActBtn onClick={() => setQuickEditOpen(true)}>
                    <ManageAccountsRoundedIcon style={{ fontSize: 15 }} /> Quick edit
                  </ActBtn>
                </Hint>
              )}
            </>
          ) : canManageTasks ? (
            <Hint title='Create a task. Schedule the recurring work that satisfies this obligation. A task obligation is satisfied by completing the work within its cadence; a document obligation is satisfied by uploading the file instead.'>
              <ActBtn $primary onClick={onCreateTask}>
                <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> Create task
              </ActBtn>
            </Hint>
          ) : null}
          {!isFile && canManage && bpId && !(obligation.formTemplateId ?? obligation.form_template_id) && (
            <GenerateFormFromDocument
              buildingPassportId={bpId}
              targetType='programme_obligation'
              targetId={obligation.id}
            >
              {open => (
                <CardFormGen type='button' onClick={open} data-tour-id='formGen.open.obligation'>
                  <UploadFileRoundedIcon style={{ fontSize: 15 }} aria-hidden='true' />
                  <span>Use your own checklist</span>
                </CardFormGen>
              )}
            </GenerateFormFromDocument>
          )}
          {isFile && (
            obligation.openTask ? (
              <>
                <Hint title='A reminder task for this document is already scheduled. Open it.'>
                  <ActBtn onClick={() => onViewTask(obligation.openTask)}>
                    <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> View task{obligation.openTask.deadline ? ` · due ${formatDate(obligation.openTask.deadline)}` : ''}
                  </ActBtn>
                </Hint>
                {obligation.openTask.assignees?.length > 0 && (
                  <AssigneeAvatars assignees={obligation.openTask.assignees} />
                )}
                {canManageTasks && (
                  <Hint title='Quickly assign people, an approver or a contractor and set the due date, without opening the full task page.'>
                    <ActBtn onClick={() => setQuickEditOpen(true)}>
                      <ManageAccountsRoundedIcon style={{ fontSize: 15 }} /> Quick edit
                    </ActBtn>
                  </Hint>
                )}
              </>
            ) : canManageTasks ? (
              <Hint title='Create a task. E.g. a reminder to obtain or renew this document (the document itself is still what satisfies the obligation).'>
                <ActBtn onClick={onCreateTask}>
                  <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> Create task
                </ActBtn>
              </Hint>
            ) : null
          )}
          {isFile && !isUnit && canDecide && (
            <Hint title='Link a document already on this building as the evidence for this obligation.'>
              <ActBtn onClick={onLinkFile}>
                <LinkRoundedIcon style={{ fontSize: 15 }} /> Link existing file
              </ActBtn>
            </Hint>
          )}
          {!isFile && canDecide && (
            <Hint title='Record that this work was already carried out. Logs a completed task at the date you give, so it counts towards the score and appears in the history.'>
              <ActBtn onClick={onMarkDone} data-tour-id='programme.mark-done-btn'>
                <TaskAltRoundedIcon style={{ fontSize: 15 }} /> Mark as already done
              </ActBtn>
            </Hint>
          )}
          <Hint title={canRaise
            ? 'Raise a remedial. A remedial is a deficiency to put right (the exception path), unlike a task which is routine, planned work. You can convert a remedial into a corrective task later.'
            : 'You don’t have permission to raise remedials on this Building Passport.'}>
            <ActBtn
              onClick={canRaise ? onRaiseRemedial : undefined}
              disabled={raiseBusy || !canRaise}
            >
              <BuildRoundedIcon style={{ fontSize: 15 }} /> Raise remedial
            </ActBtn>
          </Hint>
          {canManage && (
            <Hint title='Mark this obligation not applicable to this building. It stops counting towards the score (e.g. no gas supply, no lift).'>
              <ActBtn $muted disabled={busy} onClick={onSkip} data-tour-id='programme.mark-na'>
                <BlockRoundedIcon style={{ fontSize: 15 }} /> Not applicable
              </ActBtn>
            </Hint>
          )}
          {canManage && (
            <Hint title='Customise this obligation. Change its form, domain, how often it’s required (cadence), and how heavily it counts in the score.'>
              <ActBtn onClick={onCustomise}>
                <SettingsRoundedIcon style={{ fontSize: 15 }} /> Customise
              </ActBtn>
            </Hint>
          )}
          {isSystem && (
            <Hint title='Flag this obligation. Tell us the library rule looks out of date or wrong for this building.'>
              <ActBtn $muted onClick={onFlag}>
                <OutlinedFlagRoundedIcon style={{ fontSize: 15 }} /> Flag
              </ActBtn>
            </Hint>
          )}
        </Actions>
      )}

      {obligation.audit && (
        <CardFootRow>
          <AuditChip entries={obligation.audit} />
        </CardFootRow>
      )}

      {quickEditOpen && obligation.openTask && (
        <TaskQuickEditModal
          task={obligation.openTask}
          bpId={bpId}
          onClose={() => setQuickEditOpen(false)}
          onSaved={() => onTaskUpdated?.()}
        />
      )}
    </Card>
  )
}

export default ProgrammeBoard

const tintForScore = scoreColour

const CardFootRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`


// Shared with the rest of the app via utils/frequency.js — kept here as a
// thin re-export alias for the cards that already reference it.
const cadenceLabel = frequencyLabel

const formatDate = iso => {
  if (!iso) return 'Not set'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}


// ── animations ───────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
`

// ── layout ───────────────────────────────────────────────
const Board = styled.div`
  ${({ $embedded, theme }) => $embedded && `
    background: ${theme.colors.white};
    border: 1px solid #ececec;
    border-radius: 16px;
    padding: 18px 20px 22px;
  `}
`

const ModelDiagramWrap = styled.div`
  margin-top: 14px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`

const ToolbarTitle = styled.h2`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ToolbarScore = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
  color: ${({ $tint }) => $tint};
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ $tint }) => `${$tint}14`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
`

const ScoreHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: -2px 0 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f7f7f8;
  border: 1px solid #ececef;
  color: ${({ theme }) => theme.colors.stormGray || '#5b6270'};
  font-size: 13.5px;
  line-height: 1.45;
  svg { flex-shrink: 0; margin-top: 1px; color: #9a9aa5; }
`

const ToolbarSpacer = styled.div` flex: 1; `

const OpenFullLink = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: transparent;
  border: 1px solid #e4e4e7;
  color: ${({ theme }) => theme.colors.haiti};
  padding: 6px 13px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: background 160ms ease, border-color 160ms ease;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; border-color: #d0d0d6; }
`

const ProgrammeSubtitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin: -6px 0 10px;
`

const KindSplitLine = styled.div`
  display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin: 0 0 18px;
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: 999px;
  font-size: 12px;
  cursor: help;
`

const KindSplitItem = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  &::before {
    content: ''; width: 8px; height: 8px; border-radius: 2px;
    background: ${({ $tint }) => $tint};
  }
  strong { color: ${({ theme }) => theme.colors.haiti}; font-variant-numeric: tabular-nums; }
`

const KindSplitDot = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]}; opacity: 0.5;
`

const SettingUpChip = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #d97706;
`

const HelpLink = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: transparent;
  border: 1px solid #e4e4e7;
  color: ${({ theme }) => theme.colors.haiti};
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const HelpPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid #ececec;
  border-radius: 12px;
`

const HelpRow = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.5;
`

const HelpTerm = styled.span`
  display: inline-block;
  min-width: 124px;
  font-weight: 800;
  color: ${({ $c, theme }) => $c || theme.colors.haiti};
`

const ReadinessBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  margin-bottom: 16px;
  background: #fffaf0;
  border: 1px solid #fde68a;
  border-left: 4px solid #d97706;
  border-radius: 12px;
`

const ReadinessHead = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const ReadinessBody = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ReadinessGoals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Goal = styled.span`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  background: ${({ $done }) => $done ? 'rgba(47,163,107,0.10)' : '#fff'};
  border: 1px solid ${({ $done }) => $done ? 'rgba(47,163,107,0.30)' : '#e4e4e7'};
  color: ${({ $done }) => $done ? '#15803d' : '#6e6e78'};
  transition: border-color 140ms ease, background 140ms ease;
  &:hover { border-color: #d97706; background: ${({ $done }) => $done ? 'rgba(47,163,107,0.16)' : '#fffdf6'}; }
  &::before { content: '${({ $done }) => $done ? '✓' : '○'}'; font-weight: 800; }
`

const ReadinessActions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
`

const ReadinessCta = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  border: none; color: #fff;
  padding: 8px 16px; border-radius: 999px;
  font-size: 12.5px; font-weight: 700; cursor: pointer;
  &:hover { filter: brightness(1.06); }
`

const ReadinessGhost = styled.button`
  font: inherit;
  background: transparent;
  border: 1px solid #e4e4e7; color: ${({ theme }) => theme.colors.haiti};
  padding: 8px 16px; border-radius: 999px;
  font-size: 12.5px; font-weight: 700; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`

const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px;
  flex: 1; min-width: 220px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #e4e4e7;
  border-radius: 10px;
`

const SearchInput = styled.input`
  font: inherit;
  flex: 1; min-width: 0;
  border: none; outline: none; background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ClearSearch = styled.button`
  font: inherit;
  border: none; background: transparent; cursor: pointer;
  font-size: 18px; line-height: 1; color: #94a3b8;
  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const ControlSpacer = styled.div` flex: 1; `

const ResultCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-variant-numeric: tabular-nums;
`

const ClearLink = styled.button`
  font: inherit;
  border: none; background: transparent; cursor: pointer;
  font-size: 12px; font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  &:hover { text-decoration: underline; }
`

const CollapseAll = styled.button`
  font: inherit;
  border: 1px solid #e4e4e7; background: ${({ theme }) => theme.colors.white};
  border-radius: 999px; padding: 6px 13px; cursor: pointer;
  font-size: 12px; font-weight: 700; color: ${({ theme }) => theme.colors.haiti};
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const InlineLink = styled.button`
  font: inherit;
  border: none; background: transparent; cursor: pointer;
  font-size: inherit; font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  &:hover { text-decoration: underline; }
`

const DomainCaret = styled.span`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[800]};
  transform: rotate(${({ $open }) => $open ? '90deg' : '0deg'});
  transition: transform 200ms ease;
  margin-left: 4px;
`

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 24px;
  @media (max-width: 760px) { grid-template-columns: repeat(2, 1fr); }
`

const SummaryChip = styled.button`
  font: inherit;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ $active, $colour }) => $active ? $colour : '#ececec'};
  border-radius: 12px;
  border-left: 4px solid ${({ $colour }) => $colour};
  cursor: pointer;
  text-align: left;
  box-shadow: ${({ $active, $colour }) => $active ? `0 0 0 2px ${$colour}22` : 'none'};
  transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
  animation: ${fadeUp} 360ms ease both;
  &:hover { border-color: ${({ $colour }) => $colour}; background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const SummaryChipIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: 8px;
  background: ${({ $colour }) => `${$colour}1A`};
  color: ${({ $colour }) => $colour};
`

const SummaryChipText = styled.div`
  display: flex; flex-direction: column;
`

const SummaryChipValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
`

const SummaryChipLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 600;
  margin-top: 2px;
`

const quietPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const SkeletonSection = styled.div``

const SkeletonHeader = styled.div`
  display: flex; align-items: center; gap: 14px;
  padding: 14px 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #ececec;
  border-bottom: none;
  border-radius: 14px 14px 0 0;
`

const SkeletonStack = styled.div`
  flex: 1;
  display: flex; flex-direction: column; gap: 6px;
`

const SkeletonCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(360px, 100%), 1fr));
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #ececec;
  border-top: none;
  border-radius: 0 0 14px 14px;
`

const SkeletonCard = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px 16px;
  border: 1px solid #ececec;
  border-radius: 12px;
`

const SkeletonBlock = styled.span`
  display: block;
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '12px'};
  border-radius: ${({ $r }) => $r || '4px'};
  background: #eceef2;
  animation: ${quietPulse} 1.6s ease-in-out infinite;
`

const ReviewJump = styled.button`
  font: inherit;
  display: inline;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
`

const SlowRetry = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`

const SlowText = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RetryButton = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  color: #fff;
  padding: 7px 18px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  &:hover { filter: brightness(1.06); }
`

const EmptyState = styled.div`
  padding: 36px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 14px;
  border: 1px dashed #e4e4e7;
`

const DomainSection = styled.section`
  margin-bottom: 28px;
  animation: ${fadeUp} 420ms ease both;
  animation-delay: ${({ $stagger }) => 50 + $stagger * 60}ms;
`

const DomainHeader = styled.div`
  display: flex; align-items: center; gap: 14px;
  padding: 14px 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #ececec;
  border-bottom: ${({ $collapsed }) => $collapsed ? '1px solid #ececec' : 'none'};
  border-radius: ${({ $collapsed }) => $collapsed ? '14px' : '14px 14px 0 0'};
  border-left: 4px solid ${({ $tone }) => $tone};
  cursor: pointer;
  transition: background 160ms ease;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const DomainIconChip = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  border-radius: 12px;
  background: ${({ $tone }) => `${$tone}1A`};
  color: ${({ $tone }) => $tone};
  border: 1px solid ${({ $tone }) => `${$tone}33`};
`

const DomainHeaderText = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column;
`

const DomainName = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
`

const DomainMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 2px;
`

const DomainScore = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 28px;
  font-variant-numeric: tabular-nums;
  color: ${({ $tint }) => $tint};
  letter-spacing: -0.6px;
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(360px, 100%), 1fr));
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #ececec;
  border-top: none;
  border-radius: 0 0 14px 14px;
`

const Card = styled.div`
  position: relative;
  background: ${({ theme, $skipped }) => $skipped ? theme.colors.lighterGrey : theme.colors.white};
  border: 1px solid ${({ $skipped }) => $skipped ? '#e0e0e3' : '#ececec'};
  border-left: 3px solid ${({ $colour }) => $colour};
  border-radius: 12px;
  padding: 14px 16px 12px;
  display: flex; flex-direction: column; gap: 10px;
  scroll-margin: 90px 0;
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 400ms ease, background-color 400ms ease;
  animation: ${scaleIn} 360ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
  animation-delay: ${({ $stagger }) => 30 + $stagger * 32}ms;
  opacity: ${({ $skipped }) => $skipped ? 0.7 : 1};
  ${({ $highlight }) => $highlight && `
    background: #e6f7f9;
  `}
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(12,18,41,0.08);
  }
`

const CardHeader = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 10px;
`

const HeaderLeft = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px; min-width: 0;
`

const LegTypeBadge = styled.span`
  cursor: help;
  padding: 2px 9px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.02em;
  color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $fg }) => `${$fg}33`};
`

const CardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.35;
`

const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: ${({ $colour }) => `${$colour}1A`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`

const CardMeta = styled.div`
  display: flex; flex-wrap: wrap; gap: 14px;
  border-top: 1px solid #f3f3f5;
  border-bottom: 1px solid #f3f3f5;
  padding: 8px 0;
`

const Description = styled.p`
  margin: 0;
  font-size: 12px;
  color: #4d4d56;
  line-height: 1.5;
`

const EvidenceRow = styled.div`
  display: flex; gap: 10px; align-items: center;
  padding: 10px 12px;
  background: rgba(51,190,204,0.06);
  border: 1px solid rgba(51,190,204,0.20);
  border-radius: 10px;
`

const EvidenceIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border-radius: 8px;
  background: rgba(51,190,204,0.15);
  color: #33becc;
  flex-shrink: 0;
`

const EvidenceThumb = styled.img`
  width: 32px; height: 32px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme?.colors?.lighterGrey || '#ececec'};
`

const EvidenceText = styled.div`
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
`

const EvidenceLabel = styled.span`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #33becc;
  font-weight: 800;
`

const EvidenceName = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
`

const EvidenceSub = styled.span`
  font-size: 11px;
  color: #6e6e78;
`

const SourceBadge = styled.span`
  display: inline-flex; align-items: center; align-self: flex-start;
  margin-top: 4px; padding: 2px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.02em; cursor: help;
  color: ${({ $manual }) => ($manual ? '#a16207' : '#0e7490')};
  background: ${({ $manual }) => ($manual ? '#fef3c7' : '#e0f2fe')};
  border: 1px solid ${({ $manual }) => ($manual ? '#f2e0a8' : '#bde3f5')};
`

const PendingHint = styled.p`
  margin: 0;
  padding: 8px 12px;
  font-size: 12px;
  font-style: italic;
  color: #6e6e78;
  background: #f6f6f9;
  border: 1px dashed #dcdce0;
  border-radius: 10px;
`

const UnitListToggle = styled.button`
  font: inherit;
  align-self: flex-start;
  background: none; border: none; cursor: pointer;
  padding: 2px 0; margin-top: 6px;
  font-size: 11px; font-weight: 700;
  color: #2aa6b3;
  &:hover { text-decoration: underline; }
`

const UnitList = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  margin-top: 6px;
  max-height: 220px;
  overflow-y: auto;
`

const UnitSearch = styled.input`
  font: inherit;
  margin-top: 6px;
  padding: 6px 10px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 12px;
  outline: none;
  &:focus { border-color: #2aa6b3; }
`

const UnitListMeta = styled.div`
  margin-top: 6px;
  font-size: 10.5px;
  color: #8a8f9c;
`

const UnitListRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 11.5px;
`

const UnitListName = styled.span`
  flex: 1 1 auto;
  color: #2a2a35; font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const unitStateRag = state =>
  state === 'covered' ? RAG_LEVELS.low
    : state === 'expired' || state === 'denied' ? RAG_LEVELS.critical
      : RAG_LEVELS.medium

const UnitStateChip = styled.span`
  flex: 0 0 auto;
  padding: 1px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 800;
  color: ${({ $state }) => unitStateRag($state).fg};
  background: ${({ $state }) => unitStateRag($state).bg};
`

const UnitListExpiry = styled.span`
  flex: 0 0 auto;
  color: #6e6e78; font-size: 10.5px;
`

const UnitReviewBtns = styled.span`
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 4px;
  margin-left: auto;
`

const UnitReviewBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.02em;
  cursor: pointer;
  color: ${({ $tone }) => $tone === 'ok' ? RAG_LEVELS.low.fg : RAG_LEVELS.critical.fg};
  background: ${({ $tone }) => $tone === 'ok' ? RAG_LEVELS.low.bg : RAG_LEVELS.critical.bg};
  border: 1px solid ${({ $tone }) => $tone === 'ok' ? `${RAG_LEVELS.low.fg}33` : `${RAG_LEVELS.critical.fg}33`};
  transition: filter 150ms ease;
  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const ApproveAllUnitsBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 8px;
  padding: 8px 14px;
  min-height: 40px;
  border-radius: 8px;
  font-size: 13px; font-weight: 800; letter-spacing: 0.01em;
  cursor: pointer;
  color: #fff;
  background: ${RAG_LEVELS.low.fg};
  border: 1px solid ${RAG_LEVELS.low.fg};
  transition: filter 150ms ease;
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const UnitRemedialBtn = styled.button`
  font: inherit;
  flex: 0 0 auto;
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.02em;
  cursor: pointer;
  color: #4a4f5e;
  background: #f1f3f7;
  border: 1px solid #e6e8ec;
  transition: filter 150ms ease, border-color 150ms ease;
  &:hover:not(:disabled) { border-color: #d0d0d6; }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const UnitCoverageBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px 12px;
  background: #f6f6f9;
  border: 1px solid #ececef;
  border-radius: 10px;
`

const UnitCoverageHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`

const UnitCoverageHeadLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const UnitCoverageLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a93;
`

const UnitCoverageCount = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  font-variant-numeric: tabular-nums;
`

const UnitTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: #e6e6ea;
  overflow: hidden;
`

const UnitFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${({ $tint }) => $tint || '#33becc'};
  transition: width 480ms ease;
`

const UnitCoverageMeta = styled.span`
  font-size: 11px;
  color: #6e6e78;
  line-height: 1.4;
`

const Actions = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
  padding-top: 6px;
`

const ActBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: all 150ms ease;
  background: ${({ $primary, theme }) => ($primary ? theme.colors.secondary : '#fff')};
  color: ${({ $primary, $muted }) => ($primary ? '#fff' : $muted ? '#8A8F9C' : '#0C1229')};
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.secondary : 'rgba(12,18,41,0.16)')};
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.secondary};
    ${({ $primary }) => ($primary ? 'filter: brightness(1.06);' : 'color: #0C1229;')}
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const CardFormGen = styled(FormGenButton)`
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 14px;
`

const ReviewPrompt = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  margin-top: 8px; padding: 12px 14px;
  border: 1px solid #dbe3ea; border-left: 3px solid #475569;
  border-radius: 10px; background: #f6f8fa;
`

const ReviewText = styled.p`
  margin: 0; font-size: 12.5px; line-height: 1.5; color: #4d4d56;
  strong { color: #0C1229; font-weight: 800; }
`

const ReviewBtns = styled.div` display: flex; flex-wrap: wrap; gap: 8px; `

const ReviewFilterBanner = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  margin: 4px 0 14px; padding: 14px 16px;
  border: 1px solid #dbe3ea; border-left: 3px solid #475569;
  border-radius: 12px; background: #f6f8fa;
`
const ReviewFilterIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: #e4eaf0; color: #475569;
`
const ReviewFilterBody = styled.div` flex: 1; min-width: 0; `
const ReviewFilterTitle = styled.div`
  font-size: 13px; font-weight: 800; color: #0C1229; margin-bottom: 3px;
`
const ReviewFilterText = styled.p`
  margin: 0; font-size: 12.5px; line-height: 1.5; color: #4d4d56;
  strong { color: #0C1229; font-weight: 700; }
`

const ConfirmBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 999px; cursor: pointer;
  font-size: 12px; font-weight: 800;
  background: #15803d; color: #fff; border: 1px solid #15803d;
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const ReviewNaBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 999px; cursor: pointer;
  font-size: 12px; font-weight: 700;
  background: #fff; color: #0C1229; border: 1px solid rgba(12,18,41,0.16);
  &:hover:not(:disabled) { border-color: #b00020; color: #b00020; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const MetaLine = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  padding: 8px 0 2px;
`
const ReqLine = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 4px 0 2px;
`
const ReqLabel = styled.span`
  font-size: 10px; font-weight: 800; letter-spacing: 0.08em;
  text-transform: uppercase; color: #8a909b;
`
const MetaChip = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 999px;
  color: ${({ $tint }) => $tint || '#4A4F5E'};
  background: ${({ $tint }) => ($tint ? `${$tint}14` : '#f1f3f7')};
  border: 1px solid ${({ $tint }) => ($tint ? `${$tint}33` : '#e6e8ec')};
`
const AddObligationButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  color: #fff;
  padding: 7px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  &:hover { filter: brightness(1.06); }
`

const MapFilesButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.secondary};
  padding: 7px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  &:hover { background: ${({ theme }) => `${theme.colors.secondary}0F`}; }
`

const DomainAddBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.secondary};
  padding: 5px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  transition: background 150ms ease, color 150ms ease;
  &:hover { background: ${({ theme }) => theme.colors.secondary}; color: #fff; }
`

const SkipReason = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.45;
  color: #475569;
  padding: 8px 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  svg { flex-shrink: 0; margin-top: 1px; color: #64748b; }
`

const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 100020;
`

const ModalCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 14px;
  padding: 24px 28px;
  width: 90%; max-width: 480px;
  box-shadow: 0 20px 60px rgba(12, 18, 41, 0.25);
`

const ModalTitle = styled.h4`
  margin: 0 0 12px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ModalBody = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  color: #4d4d56;
  line-height: 1.5;
`

const ReasonInput = styled.textarea`
  width: 100%; min-height: 76px; resize: vertical;
  border: 1px solid #e0e0e0; border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit; font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.secondary}; }
`

const UnitPickerLabel = styled.label`
  display: block;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.08em;
  text-transform: uppercase; color: #8a909b;
  margin-bottom: 6px;
`

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 16px;
`

const SecondaryAction = styled.button`
  font: inherit;
  background: transparent;
  border: 1px solid #e0e0e0;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 12px; font-weight: 600;
  padding: 8px 16px; border-radius: 8px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const PrimaryAction = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 16px; border-radius: 8px;
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
`
